import { EmployeePhone } from "../models/employees/Employees";

/**
 * Helpers for the employee contact actions shown on employee fields in the
 * Work Order Details screen.
 *
 * An "employee field" is detected from the field metadata via TabWareType — the
 * WO metadata marks employee-backed value searches (Requestor / Assigned To /
 * Technician use ValueSearch__Employee; Supervisor and Next Reviewer use their
 * own employee-backed types). Contact info (email + phones) for a given
 * employee id comes from the GetDataForEmployee response.
 */

// TabWareType values whose field value is an employee id. Extend as needed.
export const EMPLOYEE_FIELD_TABWARE_TYPES = [
  "ValueSearch__Employee",
  "ValueSearch__Supervisor",
  "ValueSearch__WorkOrderReviewer",
];

/** True when a WO Details field's value is an employee (by metadata TabWareType). */
export const isEmployeeField = (field: { TabWareType?: string | null }): boolean =>
  !!field?.TabWareType && EMPLOYEE_FIELD_TABWARE_TYPES.includes(field.TabWareType);

/** Employee email from a GetDataForEmployee response, or undefined. */
export const getEmailFromEmployeeData = (data: any): string | undefined => {
  const email = data?.EmailAddress;
  return email ? String(email).trim() : undefined;
};

/** Normalizes the Phone[] array from a GetDataForEmployee response. */
export const extractPhonesFromEmployeeData = (data: any): EmployeePhone[] => {
  const raw = Array.isArray(data?.Phone)
    ? data.Phone
    : Array.isArray(data?.Phones)
    ? data.Phones
    : [];
  return raw
    .map((p: any) => ({
      EmployeeId: p.EmployeeId,
      PhoneCode: p.PhoneCode ?? p.Code,
      PhoneNumber: p.PhoneNumber ?? p.Number,
    }))
    .filter((p: EmployeePhone) => !!p.PhoneNumber);
};

/**
 * From a list of phones, the ones that are text-message capable based on the
 * device-local list of SMS-enabled Phone Codes configured in Settings.
 * Matching is exact/case-sensitive so codes that differ only by case
 * (e.g. "Business" vs "BUSINESS") stay distinct. When no codes are
 * configured, nothing is textable.
 */
export const filterTextablePhones = (
  phones: EmployeePhone[] | undefined,
  smsCapablePhoneCodes: string[] | undefined
): EmployeePhone[] => {
  const codes = smsCapablePhoneCodes ?? [];
  if (codes.length === 0) return [];
  return (phones ?? []).filter(
    (p) => !!p.PhoneCode && codes.includes(p.PhoneCode)
  );
};

/** Digits-only version of a phone number for use in tel:/sms: URIs. */
export const sanitizePhoneForUri = (phone?: string): string =>
  (phone ?? "").replace(/[^\d+]/g, "");
